import { Money, type MonetaryValue } from '@shared/lib/money/money'
import Decimal from 'decimal.js'
import { currencyFractionDigits } from '@shared/lib/format/currency'

export interface SplitPreviewInput {
  readonly amount: MonetaryValue
  readonly vendepayFee: MonetaryValue
  readonly affiliatePercentage?: string
  readonly coProducerPercentage?: string
}

export interface SplitPreview {
  readonly producer: MonetaryValue
  readonly affiliate: MonetaryValue
  readonly coProducer: MonetaryValue
  readonly vendepayFee: MonetaryValue
  readonly isInvalid: boolean
}

export function computeSplitPreview(input: SplitPreviewInput): SplitPreview {
  if (
    percentageOrZero(input.affiliatePercentage) > 0 &&
    percentageOrZero(input.coProducerPercentage) > 0
  ) {
    return combinedSplitPreview(input)
  }
  const amount = Money.fromMonetaryValue(input.amount)
  const zero = new Money('0.000', amount.currency)
  const vendepayFee = Money.fromMonetaryValue(input.vendepayFee)
  const affiliate =
    percentageOrZero(input.affiliatePercentage) > 0
      ? amount.percent(input.affiliatePercentage ?? '0')
      : zero
  const afterAffiliateAndFee = amount.subtract(vendepayFee).subtract(affiliate)
  const coProducer =
    percentageOrZero(input.coProducerPercentage) > 0
      ? afterAffiliateAndFee.percent(input.coProducerPercentage ?? '0')
      : zero
  const producer = afterAffiliateAndFee.subtract(coProducer)

  return {
    producer: producer.toMonetaryValue(),
    affiliate: affiliate.toMonetaryValue(),
    coProducer: coProducer.toMonetaryValue(),
    vendepayFee: vendepayFee.toMonetaryValue(),
    isInvalid: producer.isNegative() || coProducer.isNegative() || affiliate.isNegative(),
  }
}

function combinedSplitPreview(input: SplitPreviewInput): SplitPreview {
  const currency = input.amount.currency
  if (currency !== input.vendepayFee.currency) throw new Error('Moedas incompatíveis na prévia.')
  const close = (value: Decimal) =>
    value.toDecimalPlaces(currencyFractionDigits(currency), Decimal.ROUND_HALF_UP)
  const money = (value: Decimal): MonetaryValue => ({ amount: value.toFixed(3), currency })
  const amount = close(new Decimal(input.amount.amount))
  const vendepayFee = close(new Decimal(input.vendepayFee.amount))
  const grossAffiliate = close(amount.mul(input.affiliatePercentage ?? '0').div(100))
  const affiliateFee = close(vendepayFee.mul(input.affiliatePercentage ?? '0').div(100))
  const affiliate = grossAffiliate.minus(affiliateFee)
  const producerBase = amount.minus(vendepayFee).minus(affiliate)
  const coProducer = close(producerBase.mul(input.coProducerPercentage ?? '0').div(100))
  const producer = producerBase.minus(coProducer)
  return {
    producer: money(producer),
    affiliate: money(affiliate),
    coProducer: money(coProducer),
    vendepayFee: money(vendepayFee),
    isInvalid: producer.isNegative() || coProducer.isNegative() || affiliate.isNegative(),
  }
}

function percentageOrZero(value: string | undefined): number {
  const parsed = Number.parseFloat(value ?? '0')
  return Number.isFinite(parsed) ? parsed : 0
}
